import React, { useEffect, useRef, useState } from "react";

import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import { Stack } from "@mui/material";

import api from "../../services/api";

const useStyles = makeStyles(() => ({
  root: { display: "flex", flexWrap: "wrap" },
  dialogPaper: {
    borderRadius: 14,
    border: "1px solid rgba(17, 24, 39, 0.08)",
    boxShadow: "0 18px 60px rgba(16, 24, 40, 0.18)",
    overflow: "hidden"
  },
  dialogTitle: {
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  titleWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 2
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827"
  },
  subtitle: {
    fontSize: 12.5,
    color: "#6B7280"
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: "1px solid rgba(17, 24, 39, 0.08)"
  },
  dialogContent: {
    padding: 20,
    background:
      "linear-gradient(180deg, rgba(249,250,251,0.6) 0%, rgba(255,255,255,1) 100%)",
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#fff"
    }
  },
  dialogActions: {
    padding: "14px 20px",
    borderTop: "1px solid rgba(17, 24, 39, 0.06)",
    gap: 10
  },
  primaryButton: {
    borderRadius: 12,
    textTransform: "none",
    boxShadow: "0 6px 18px rgba(59, 130, 246, 0.25)"
  },
  secondaryButton: {
    borderRadius: 12,
    textTransform: "none"
  }
}));

const FlowBuilderAddSwitchFlowModal = ({
  open,
  onSave,
  onUpdate,
  data,
  close,
  currentFlowId
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [flows, setFlows] = useState([]);
  const [selectedFlowId, setSelectedFlowId] = useState("");

  useEffect(() => {
    if (open !== "create" && open !== "edit") {
      return undefined;
    }

    const loadFlows = async () => {
      try {
        setLoading(true);
        const { data: response } = await api.get("/flowbuilder");
        const flowList = Array.isArray(response?.flows) ? response.flows : [];
        const filteredFlows = flowList.filter(
          item => Number(item.id) !== Number(currentFlowId)
        );

        setFlows(filteredFlows);

        const existingId =
          data?.data?.flowSelected?.id || data?.data?.flowId || "";
        setSelectedFlowId(existingId ? String(existingId) : "");
        setActiveModal(true);
      } catch (error) {
        toast.error("Nao foi possivel carregar os fluxos");
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    loadFlows();

    return () => {
      isMounted.current = false;
    };
  }, [open, data, currentFlowId]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSave = () => {
    const selected = flows.find(item => Number(item.id) === Number(selectedFlowId));

    if (!selected) {
      return toast.error("Selecione um fluxo valido");
    }

    const payload = {
      flowId: selected.id,
      flowSelected: {
        id: selected.id,
        name: selected.name
      }
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: payload
      });
    } else {
      onSave(payload);
    }

    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <div className={classes.titleWrapper}>
            <Typography className={classes.title}>
              {open === "create" ? "Adicionar troca de fluxo" : "Editar troca de fluxo"}
            </Typography>
            <Typography className={classes.subtitle}>
              Escolha o fluxo de destino que sera iniciado em seguida
            </Typography>
          </div>

          <IconButton
            onClick={handleClose}
            className={classes.closeButton}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.dialogContent} dividers>
          {loading ? (
            <Stack alignItems="center" padding={2}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <FormControl fullWidth variant="outlined">
              <InputLabel id="flow-switch-select-label">Fluxo de destino</InputLabel>
              <Select
                labelId="flow-switch-select-label"
                value={selectedFlowId}
                onChange={e => setSelectedFlowId(String(e.target.value))}
                label="Fluxo de destino"
              >
                {flows.map(flow => (
                  <MenuItem key={flow.id} value={String(flow.id)}>
                    {flow.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleClose}
            color="secondary"
            variant="outlined"
            className={classes.secondaryButton}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            className={classes.primaryButton}
            disabled={loading}
          >
            {open === "create" ? "Adicionar" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddSwitchFlowModal;
